import { useContext, useEffect, useState } from "react"
import { Col, Div, Label, Row, Switch, Text } from "atomize"
import { toast } from "react-toastify"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEuro } from "@fortawesome/free-solid-svg-icons"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import TextInput from "../inputs/TextInput"
import InlineCheckboxButtons from "../InlineCheckboxButtons"
import { theme } from "../settings/config"
import { playersContext } from "../providers/players.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	BILLED_TO_SCHOOL,
	getLessonBilledTo,
	getSchoolRates,
	isPaidToSchool,
	isSubscriptionCourse,
	PAID_TO_SCHOOL,
	PAID_TO_SCHOOL_CHOICE,
} from "../utils/billing.utils"
import {
	findKnownPlayer,
	getPaymentMethodName,
	getPlayerFullName,
	getPlayerLabel,
	courseInSentence,
	getPtrLevel,
	lessonTypeDisplayNameOf,
	lessonTypeHasPtrLevel,
	normalizeName,
	paymentCoveredElsewhere,
} from "../utils/func.utils"
import { resolvePartecipants, setLessonSubstitute } from "../utils/firestore.utils"
import {
	getSubstitutePrice,
	substituteOf,
	substitutePaidToFor,
} from "../utils/substitutes.utils"
import useRecoveriesLeft from "../lesson/useRecoveriesLeft"
import useSubstituteSuggestions from "../lesson/useSubstituteSuggestions"
import SubstituteSuggestions from "../lesson/SubstituteSuggestions"
import PaidToChoice from "../lesson/PaidToChoice"

// I recuperi si contano senza questa lezione: se la sta recuperando qui,
// quello che gli resta è uno in meno.
const recoveriesLabel = (left, recoveringHere) => {
	if (!recoveringHere)
		return left === 1
			? "Ha 1 lezione da recuperare in questa stagione"
			: `Ha ${left} lezioni da recuperare in questa stagione`
	const after = left - 1
	if (after === 0) return "Con questo recupero non gliene restano altri"
	return after === 1
		? "Con questo recupero gliene resta 1 in questa stagione"
		: `Con questo recupero gliene restano ${after} in questa stagione`
}

// Chi viene al posto di un allievo assente, e come paga. Il sostituto non è
// un allievo in più: la lezione resta del suo tipo e al suo prezzo.
export function AddSubstituteModal({ closeModal, lessonData, absent }) {
	const { activePlayers } = useContext(playersContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool, getSchoolSettings } = useContext(schoolContext)
	const { getPaymentMethod, getLessonType, lessonTypes } = useContext(sharedDataContext)
	const schoolSettings = getSchoolSettings()
	const rates = getSchoolRates(coach?.billingData, selectedSchool)

	// Fuori dai corsi il sostituto è un allievo come gli altri, e anche lui
	// può aver pagato il circolo invece di te. Nei corsi chi lo incassa lo
	// dice la regola del corso.
	const canPaySchool =
		!isSubscriptionCourse(lessonData.lessonType) &&
		getLessonBilledTo(lessonData, rates) !== BILLED_TO_SCHOOL
	const paidToSchoolByDefault = isPaidToSchool(schoolSettings.playersPayTo)
	const isCourse = isSubscriptionCourse(lessonData.lessonType)
	const coursePaidTo = substitutePaidToFor(
		lessonData.lessonType,
		getLessonType(lessonData.lessonType),
	)

	const current = substituteOf(lessonData.partecipants, absent)
	const [search, setSearch] = useState(current?.playerName ?? "")
	const [player, setPlayer] = useState(
		current ? { playerID: current.playerID, playerName: current.playerName } : null,
	)
	// Come paga, o "Al circolo": chi paga il circolo un metodo non ce l'ha.
	// Un sostituto nuovo parte da a chi pagano di solito gli allievi.
	const [paymentMethod, setPaymentMethod] = useState(
		canPaySchool && (current ? isPaidToSchool(current.paidTo) : paidToSchoolByDefault)
			? PAID_TO_SCHOOL_CHOICE
			: (current?.paymentMethod ?? null),
	)
	const [paymentAmount, setPaymentAmount] = useState(current?.paymentAmount ?? null)
	// Nei corsi chi paga la volta paga il circolo: chi ha preso i soldi, si
	// parte da quello che dice il corso
	const [visitPaidTo, setVisitPaidTo] = useState(current?.paidTo ?? coursePaidTo)
	const [isSaving, setSavingState] = useState(false)

	const lessonStart = lessonData.lessonStart.toDate()
	const recoveries = useRecoveriesLeft(
		[player?.playerID],
		lessonStart,
		lessonData.lessonID,
		lessonData.lessonType,
	)
	const recoveriesKnown = player ? recoveries[player.playerID] : undefined
	// il recupero già scelto resta proponibile: è quello che si sta guardando
	const canRecover = recoveriesKnown > 0 || paymentMethod === "recovery"

	// chi ha lezioni da recuperare di solito viene per quello — anche se di
	// solito gli allievi pagano il circolo: recuperando non paga nessuno
	const choseOnlyTheDefault = !current && paymentMethod === PAID_TO_SCHOOL_CHOICE
	useEffect(() => {
		if (recoveriesKnown > 0 && (!paymentMethod || choseOnlyTheDefault)) {
			setPaymentMethod("recovery")
			setPaymentAmount(0)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recoveriesKnown])

	const listPrice = getSubstitutePrice(lessonData, {
		lessonPrices: schoolSettings.lessonPrices,
		courtsFee: schoolSettings.courtsFee,
		rates,
		lessonTypes,
	})
	// il prezzo parte dal listino finché nessuno l'ha scritto
	const shownAmount = paymentAmount ?? listPrice

	// chi è già nella lezione non può sostituire nessuno
	const inLesson = new Set(
		(lessonData.partecipants || [])
			.filter(p => p.playerID && p.playerID !== current?.playerID)
			.map(p => p.playerID),
	)
	// L'elenco lo mostra il telefono, come nell'inserimento di una lezione:
	// sta sopra la tastiera, dove un elenco nostro sotto al campo finiva
	// coperto.
	const candidates = activePlayers.filter(p => !inLesson.has(p.playerID))
	const suggestions = useSubstituteSuggestions(lessonData, absent, inLesson)
	const groupLevel = getPtrLevel(lessonData.ptrLevel)

	// Un sostituto recupera o paga la volta: l'abbonamento è di chi il corso
	// lo fa, e il recupero è dell'app, non del catalogo del circolo.
	const paymentMethods = [
		...(canRecover ? ["recovery"] : []),
		...(schoolSettings.availablePaymentMethods || []).filter(
			method => method !== "recovery" && method !== "subscription",
		),
	]
	// "Ha pagato al circolo" è un interruttore sotto i metodi, non uno di
	// loro: in fondo alla riga finiva fuori dallo schermo
	const paysSchool = paymentMethod === PAID_TO_SCHOOL_CHOICE
	const togglePaysSchool = () => {
		setPaymentMethod(paysSchool ? null : PAID_TO_SCHOOL_CHOICE)
		setPaymentAmount(paysSchool ? null : listPrice)
	}

	// Si sceglie dall'elenco: il campo porta l'etichetta con il soprannome,
	// che distingue due omonimi, e da lì si risale all'allievo. Scritto a
	// mano lo si riconosce lo stesso — nome completo, etichetta o soprannome
	// — lasciando il testo com'è. Un nome che non è di nessuno è un allievo
	// nuovo, come nel modulo della lezione: nasce quando si salva.
	const choose = (text, known) => {
		const typed = text.trim()
		setSearch(text)
		setPlayer(
			known
				? { playerID: known.playerID, playerName: getPlayerFullName(known) }
				: typed
					? { playerID: "", playerName: typed }
					: null,
		)
		setPaymentMethod(canPaySchool && paidToSchoolByDefault ? PAID_TO_SCHOOL_CHOICE : null)
		setPaymentAmount(null)
		setVisitPaidTo(coursePaidTo)
	}

	const handleSearchChange = e => {
		const value = e.target.value
		const chosen = candidates.find(candidate => getPlayerLabel(candidate) === value)
		const known = chosen ?? findKnownPlayer(value, candidates)
		choose(chosen ? getPlayerFullName(chosen) : value, known)
	}

	// una proposta toccata è come un nome scelto dall'elenco
	const pickSuggestion = suggested => choose(getPlayerFullName(suggested), suggested)

	const paysVisit = isCourse && Boolean(paymentMethod) && !paymentCoveredElsewhere(paymentMethod)

	const choosePaymentMethod = method => {
		setPaymentMethod(method)
		setPaymentAmount(paymentCoveredElsewhere(method) ? 0 : listPrice)
	}

	const handleSave = async () => {
		setSavingState(true)
		try {
			// il nome nuovo si cerca e si crea come quelli della lezione, così
			// scritto due volte resta un allievo solo
			const [substitute] = player.playerID
				? [player]
				: await resolvePartecipants(
						[{ playerName: player.playerName }],
						lessonData.schoolID ?? selectedSchool,
						activePlayers,
					)
			// scritto a mano, il nome può essere di chi nella lezione c'è già
			if (inLesson.has(substitute.playerID)) {
				toast.error(`${substitute.playerName} è già in questa lezione`, {
					toastId: "substitute-already-in-lesson",
					position: "top-center",
				})
				return
			}

			await setLessonSubstitute(lessonData.lessonID, absent, {
				playerID: substitute.playerID,
				playerName: substitute.playerName,
				paymentMethod: paysSchool ? "" : paymentMethod,
				paymentAmount: Number(shownAmount) || 0,
				paymentAmountIsCustom: Number(shownAmount) !== listPrice,
				// nei corsi chi ha preso i soldi di chi paga la volta lo si
				// sceglie qui, e chi recupera tiene la regola di quando lo si
				// è segnato; fuori, conta se ha pagato il circolo
				paidTo: canPaySchool
					? paysSchool
						? PAID_TO_SCHOOL
						: null
					: paysVisit
						? visitPaidTo
						: (current?.paidTo ?? coursePaidTo),
			})
			closeModal()
		} catch {
			toast.error("Non sono riuscito a salvare il sostituto", {
				toastId: "substitute-not-saved",
				position: "top-center",
			})
		} finally {
			setSavingState(false)
		}
	}

	return (
		<>
			<Container>
				<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
					Sostituto di {absent.playerName}
				</Text>
				<Text textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					{lessonTypeDisplayNameOf(getLessonType(lessonData.lessonType), lessonData.lessonType)} ·{" "}
					{/* il livello del gruppo, quello con cui si guardano le proposte */}
					{groupLevel && `${groupLevel.label} · `}
					{DateTime.fromJSDate(lessonStart).toFormat("dd/MM, HH:mm")}
				</Text>
			</Container>

			{/* Il titolo e i bottoni restano fermi, e in mezzo si scorre: aperte
			    tutte le proposte, il foglio andava oltre lo schermo e non si
			    arrivava più ai bottoni (come negli altri fogli lunghi) */}
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
						<Paragraph title="Chi viene al suo posto">
							<TextInput
								list="substitute_list"
								value={search}
								placeholder="Cerca un allievo o scrivi un nome"
								handleChange={handleSearchChange}
							/>
							{/* scritto il soprannome, si vede chi è */}
							{player?.playerID &&
								normalizeName(player.playerName) !== normalizeName(search) && (
									<Text
										textSize="caption"
										textColor={theme.colors.green}
										m={{ t: "0.25rem" }}>
										{"\u2713"} {player.playerName}
									</Text>
								)}
							{/* un nome sbagliato si vede qui, prima di salvare un doppione */}
							{player && !player.playerID && (
								<Text
									textSize="caption"
									textColor={theme.colors.text_light}
									m={{ t: "0.25rem" }}>
									Nuovo allievo
								</Text>
							)}
							<datalist id="substitute_list">
								{candidates.map(candidate => (
									<option key={candidate.playerID} value={getPlayerLabel(candidate)} />
								))}
							</datalist>
							{/* scritto un nome le proposte si tolgono, e cancellandolo
							    tornano: sotto c'è "Come paga" per chi si è scelto */}
							{!search.trim() && (
								<SubstituteSuggestions
									suggestions={suggestions}
									onPick={pickSuggestion}
							courseInSentence={courseInSentence(
								lessonData.lessonType,
								getLessonType(lessonData.lessonType),
							)}
									canSetGroupLevel={lessonTypeHasPtrLevel(lessonData.lessonType)}
									absentName={absent.playerName?.trim()}
								/>
							)}
						</Paragraph>

						{player && (
							<Paragraph title="Come paga">
								{recoveriesKnown > 0 && (
									<Text textSize="caption" textColor={theme.colors.gunmetal} m={{ b: "0.5rem" }}>
										{recoveriesLabel(recoveriesKnown, paymentMethod === "recovery")}
									</Text>
								)}
								{/* chi ha pagato il circolo un metodo non ce l'ha */}
								{!paysSchool && (
									<InlineCheckboxButtons
										elements={paymentMethods.map(method => ({
											text: getPaymentMethodName(getPaymentMethod, method),
											type: method,
										}))}
										selectedElem={paymentMethod}
										handleClick={choosePaymentMethod}
										valueToSet="type"
									/>
								)}
								{canPaySchool && paymentMethod !== "recovery" && (
									<Label
										onClick={togglePaysSchool}
										display="flex"
										justify="space-between"
										align="center"
										m={{ b: "1rem" }}>
										<Text textSize="subheader">Ha pagato al circolo</Text>
										<Switch
											m={{ l: "0.5rem" }}
											shadow="0"
											checked={paysSchool}
											activeColor="gunmetal"
											inactiveColor="gray_light"
										/>
									</Label>
								)}
								{paysVisit && (
									<PaidToChoice
										selected={visitPaidTo}
										onChange={setVisitPaidTo}
										accent={theme.colors.gunmetal_fill}
									/>
								)}
								{paymentMethod && !paymentCoveredElsewhere(paymentMethod) && (
									<Div w="50%">
										<Paragraph title="Paga">
											<TextInput
												type="number"
												value={shownAmount ?? ""}
												handleChange={e => setPaymentAmount(e.target.value)}
												suffix={
													<Div
														pos="absolute"
														bg="transparent"
														top="1rem"
														right="0.5rem">
														<FontAwesomeIcon icon={faEuro} />
													</Div>
												}
											/>
										</Paragraph>
									</Div>
								)}
							</Paragraph>
						)}
				</Container>
			</Div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text={isSaving ? "Sto salvando..." : "Annulla"}
							textSize="subheader"
							action={() => !isSaving && closeModal()}
						/>
					</Col>
					{!isSaving && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.primary}
								textColor="white"
								textSize="subheader"
								text={current ? "Cambia sostituto" : "Aggiungi sostituto"}
								disabled={!player || !paymentMethod}
								action={handleSave}
							/>
						</Col>
					)}
				</Row>
			</Div>
		</>
	)
}

export default AddSubstituteModal
